"use client";
import {
  AbsoluteFill,
  Sequence,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

// ════════════════════════════════════════════════════════════════════
// LAY'S FULL PREVIEW — transparent, word-stamp synced.
// 16fps · 1280x704 · base video 759.305s.
// Palette: primary=golden yellow #FFC72C, accent_gold=Lay's red #E61A27
// production_id: c56119b3-932c-4bb6-b201-3952277c7031
// ════════════════════════════════════════════════════════════════════

const FPS = 16;
const sToF = (s: number) => Math.round(s * FPS);
const FONT = "Inter, system-ui, sans-serif";
const TS = "0 3px 14px rgba(0,0,0,0.92), 0 1px 5px rgba(0,0,0,0.95), 0 0 2px rgba(0,0,0,0.9)";

// Palette from spec
const PRIMARY    = "#FFC72C"; // golden yellow
const SECONDARY  = "#f5f0e8";
const TERTIARY   = "#c0c4cc";
const ACCENT_GOLD = "#E61A27"; // Lay's red
const DIM        = "#8a8f99";
const TEXT       = "#ffffff";
const WHITE      = "#ffffff";

const appear = (t: number, atS: number, dur = 0.18) =>
  interpolate(t, [atS, atS + dur], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

const win = (t: number, inS: number, outS: number) =>
  interpolate(t, [inS, inS + 0.2, outS - 0.4, outS], [0, 1, 1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

// ─── 1 · STAMP — rotated framed callout ───────────────────────────────
const Stamp: React.FC<{ s: number; title: string; sub?: string; atS: number; outS: number; color?: string; rotate?: number }> = ({ s, title, sub, atS, outS, color = PRIMARY, rotate = -8 }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = s + frame / FPS;
  const op = win(t, atS, outS);
  const sc = spring({ frame: Math.max(0, frame - sToF(atS - s)), fps, config: { damping: 9, stiffness: 130 } });
  const rot = interpolate(t, [atS, atS + 0.35], [rotate - 6, rotate], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ transform: `rotate(${rot}deg) scale(${sc})`, border: `4px solid ${color}`, padding: "22px 48px", color, boxShadow: `0 0 28px ${color}55, 0 8px 26px rgba(0,0,0,0.7)`, background: "rgba(0,0,0,0.45)" }}>
        <div style={{ fontSize: 50, fontWeight: 900, letterSpacing: "0.07em", textShadow: TS }}>{title}</div>
        {sub && <div style={{ fontSize: 18, marginTop: 7, letterSpacing: "0.2em", textAlign: "center", textShadow: TS, color: WHITE }}>{sub}</div>}
      </div>
    </AbsoluteFill>
  );
};

// ─── 2 · TEXT HIGHLIGHT — keyword punches in on its word ──────────────
const TextHighlight: React.FC<{ s: number; pre: string; hi: string; post: string; hiS: number; outS: number; hiColor?: string }> = ({ s, pre, hi, post, hiS, outS, hiColor = PRIMARY }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const preS = hiS - 0.3;
  const op = win(t, preS, outS);
  const hiOp = appear(t, hiS, 0.14);
  const hiSc = interpolate(t, [hiS, hiS + 0.25], [0.78, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ textAlign: "center", maxWidth: 1100 }}>
        {pre && <span style={{ color: DIM, fontSize: 36, fontWeight: 500, textShadow: TS, opacity: appear(t, preS, 0.2) }}>{pre} </span>}
        <span style={{ color: hiColor, fontSize: 64, fontWeight: 900, letterSpacing: "0.04em", textShadow: `0 0 22px ${hiColor}77, ${TS}`, opacity: hiOp, display: "inline-block", transform: `scale(${hiSc})` }}>{hi}</span>
        {post && <span style={{ color: DIM, fontSize: 36, fontWeight: 500, textShadow: TS, opacity: appear(t, hiS + 0.25, 0.2) }}> {post}</span>}
      </div>
    </AbsoluteFill>
  );
};

// ─── 3 · HOOK TEXT — single punchy line ───────────────────────────────
const HookText: React.FC<{ s: number; text: string; atS: number; outS: number }> = ({ s, text, atS, outS }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const op = win(t, atS, outS);
  const sc = interpolate(t, [atS, atS + 0.3], [0.9, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ color: WHITE, fontSize: 64, fontWeight: 900, letterSpacing: "0.04em", textShadow: TS, transform: `scale(${sc})`, textAlign: "center", lineHeight: 1.08 }}>
        {text}
      </div>
    </AbsoluteFill>
  );
};

// ─── 4 · LOWER THIRD — bottom-left, with accent bar ───────────────────
const LowerThird: React.FC<{
  s: number; name: string; sub: string; italic?: string; nameS: number; subS: number; outS: number;
}> = ({ s, name, sub, italic, nameS, subS, outS }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const op = win(t, nameS - 0.2, outS);
  const slideX = interpolate(t, [nameS - 0.25, nameS + 0.15], [-260, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: (v) => 1 - Math.pow(1 - v, 3),
  });
  const nameOp = appear(t, nameS, 0.16);
  const subOp = appear(t, subS, 0.2);
  const accentW = interpolate(t, [nameS - 0.2, nameS + 0.35], [0, 168], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ opacity: op }}>
      <div style={{ position: "absolute", bottom: 86, left: 64, transform: `translateX(${slideX}px)`, fontFamily: FONT }}>
        <div style={{ height: 3, width: accentW, background: PRIMARY, marginBottom: 10, boxShadow: `0 0 10px ${PRIMARY}99` }} />
        <div style={{ color: WHITE, fontSize: 34, fontWeight: 800, letterSpacing: "-0.4px", textShadow: TS, lineHeight: 1.05, opacity: nameOp }}>{name}</div>
        <div style={{ color: PRIMARY, fontSize: 16, fontWeight: 700, letterSpacing: "0.18em", marginTop: 6, textShadow: TS, opacity: subOp }}>{sub}</div>
        {italic && <div style={{ color: TERTIARY, fontSize: 13, fontStyle: "italic", marginTop: 4, textShadow: TS, opacity: subOp }}>{italic}</div>}
      </div>
    </AbsoluteFill>
  );
};

// ─── 5 · BIG STAT — number counts up and locks on the value word ──────
const BigStat: React.FC<{
  s: number; label: string; value: number; valueS: number; outS: number;
  decimals?: number; prefix?: string; suffix?: string; sub?: string; color?: string;
}> = ({ s, label, value, valueS, outS, decimals = 0, prefix, suffix, sub, color = WHITE }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = s + frame / FPS;
  const labelS = valueS - 0.25;
  const op = win(t, labelS, outS);
  const cur = interpolate(t, [valueS, valueS + 0.42], [0, value], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const numOp = appear(t, valueS, 0.12);
  const sc = spring({ frame: Math.max(0, frame - sToF(valueS - s)), fps, config: { damping: 13, stiffness: 95 } });
  const subOp = appear(t, valueS + 0.5, 0.3);
  const shown = value >= 100 ? Math.round(cur).toLocaleString() : cur.toFixed(decimals);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ color: PRIMARY, fontSize: 25, letterSpacing: "0.24em", marginBottom: 14, textShadow: TS, opacity: appear(t, labelS, 0.2), fontWeight: 700 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, opacity: numOp, transform: `scale(${sc})` }}>
        {prefix && <span style={{ color, fontSize: 52, fontWeight: 800, textShadow: TS }}>{prefix}</span>}
        <span style={{ color, fontSize: 168, fontWeight: 800, letterSpacing: "-4px", textShadow: TS, lineHeight: 1 }}>{shown}</span>
        {suffix && <span style={{ color: PRIMARY, fontSize: 56, fontWeight: 800, textShadow: TS }}>{suffix}</span>}
      </div>
      {sub && <div style={{ color: SECONDARY, fontSize: 21, letterSpacing: "0.16em", marginTop: 20, textShadow: TS, opacity: subOp }}>{sub}</div>}
    </AbsoluteFill>
  );
};

// ─── 6 · CHAPTER — STEP 01..06 / TITLE ───────────────────────────────
const Chapter: React.FC<{ s: number; num: string; title: string; sub?: string; numS: number; titleS: number; outS: number; labelText?: string }> = ({ s, num, title, sub, numS, titleS, outS, labelText = "STEP" }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = s + frame / FPS;
  const labelS = numS - 0.3;
  const op = win(t, labelS, outS);
  const numScale = spring({ frame: Math.max(0, frame - sToF(numS - s)), fps, config: { damping: 12, stiffness: 85 } });
  const numOp = appear(t, numS, 0.12);
  const lineW = interpolate(t, [titleS - 0.15, titleS + 0.5], [0, 150], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const titleOp = appear(t, titleS, 0.16);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", flexDirection: "column", opacity: op }}>
      <p style={{ color: PRIMARY, fontSize: 21, fontWeight: 700, letterSpacing: "0.32em", textTransform: "uppercase", margin: 0, marginBottom: 12, textShadow: TS, opacity: appear(t, labelS, 0.2) }}>{labelText}</p>
      <h1 style={{ color: WHITE, fontSize: "9rem", fontWeight: 800, margin: 0, lineHeight: 1, transform: `scale(${numScale})`, textShadow: TS, opacity: numOp }}>{num}</h1>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 20 }}>
        <div style={{ height: 1, width: lineW, backgroundColor: PRIMARY }} />
        <div style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: PRIMARY, opacity: titleOp, boxShadow: `0 0 8px ${PRIMARY}` }} />
        <div style={{ height: 1, width: lineW, backgroundColor: PRIMARY }} />
      </div>
      <h2 style={{ color: SECONDARY, fontSize: "2.5rem", fontWeight: 800, letterSpacing: "0.05em", margin: 0, marginTop: 12, textShadow: TS, opacity: titleOp }}>{title}</h2>
      {sub && <p style={{ color: TERTIARY, fontSize: "1.15rem", marginTop: 9, letterSpacing: "0.14em", textShadow: TS, opacity: appear(t, titleS + 0.3, 0.25) }}>{sub}</p>}
    </AbsoluteFill>
  );
};

// ─── 7 · EQUATION STACK — line1 / op / line2 ──────────────────────────
const EquationStack: React.FC<{
  s: number; l1: string; op: string; l2: string; l1S: number; opS: number; l2S: number; outS: number; opColor?: string;
}> = ({ s, l1, op, l2, l1S, opS, l2S, outS, opColor = PRIMARY }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const wOp = win(t, l1S, outS);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: wOp }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
        <div style={{ color: WHITE, fontSize: 56, fontWeight: 900, letterSpacing: "0.03em", textShadow: TS, opacity: appear(t, l1S, 0.16), transform: `scale(${interpolate(t, [l1S, l1S + 0.25], [0.85, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" })})` }}>{l1}</div>
        <div style={{ color: opColor, fontSize: 46, fontWeight: 900, textShadow: TS, opacity: appear(t, opS, 0.14) }}>{op}</div>
        <div style={{ color: WHITE, fontSize: 56, fontWeight: 900, letterSpacing: "0.03em", textShadow: TS, opacity: appear(t, l2S, 0.16), transform: `scale(${interpolate(t, [l2S, l2S + 0.25], [0.85, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" })})` }}>{l2}</div>
      </div>
    </AbsoluteFill>
  );
};

// ─── 8 · END CARD ─────────────────────────────────────────────────────
const EndCard: React.FC<{ s: number; atS: number; outS: number; title?: string; sub?: string }> = ({ s, atS, outS, title, sub }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const op = win(t, atS, outS);
  const subOp = appear(t, atS + 0.6, 0.3);
  const nextOp = appear(t, atS + 1.4, 0.3);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ color: PRIMARY, fontSize: 26, letterSpacing: "0.32em", marginBottom: 22, textShadow: TS, fontWeight: 800 }}>FACTORY FILES</div>
      {title && <div style={{ color: WHITE, fontSize: 60, fontWeight: 900, letterSpacing: "-1px", textShadow: TS, textAlign: "center", lineHeight: 1.12 }}>{title}</div>}
      {sub && <div style={{ color: SECONDARY, fontSize: 22, letterSpacing: "0.16em", marginTop: 26, textShadow: TS, opacity: subOp }}>{sub}</div>}
      <div style={{ color: TERTIARY, fontSize: 14, letterSpacing: "0.24em", marginTop: 14, textShadow: TS, opacity: nextOp }}>SUBSCRIBE · NEW VIDEO EVERY WEEK</div>
    </AbsoluteFill>
  );
};

// ════════════════════════════════════════════════════════════════════
// MASTER TIMELINE — every window tied to Lay's word_timestamps
// ════════════════════════════════════════════════════════════════════
type Item = { from: number; to: number; node: (s: number) => React.ReactNode };

const ITEMS: Item[] = [
  // item 0 · HookText · upper-center · 0.4-4.0
  { from: 0.4, to: 4.0, node: (s) => <HookText s={s} text="PEPSICO SURVEY · 2024" atS={0.755} outS={4.0} /> },
  // item 1 · BigStat · center · 8.4-13.5
  { from: 8.4, to: 13.5, node: (s) => <BigStat s={s} label="COULD NOT ANSWER" value={42} valueS={8.742} decimals={0} suffix="%" sub="what potato chips are made of" outS={13.5} /> },
  // item 2 · Stamp · lower-center → center · 20.0-23.3
  { from: 20.0, to: 23.3, node: (s) => <Stamp s={s} title="MADE WITH REAL POTATOES" sub="PRINTED ON THE BAG" atS={20.34} outS={23.3} color={PRIMARY} rotate={-6} /> },
  // item 3 · TextHighlight · center · 29.0-34.3
  { from: 29.0, to: 34.3, node: (s) => <TextHighlight s={s} pre="buried among the golden chips," hi="A BRIGHT GREEN ONE" post="" hiS={31.289} hiColor={ACCENT_GOLD} outS={34.3} /> },
  // item 4 · Stamp · center · 35.2-38.4
  { from: 35.2, to: 38.4, node: (s) => <Stamp s={s} title="TRACKING DEVICE" sub="PLACED IN THE FRYER ON PURPOSE" atS={35.584} outS={38.4} color={PRIMARY} rotate={-8} /> },
  // item 5 · LowerThird · lower-center · 45.0-47.7
  { from: 45.0, to: 47.7, node: (s) => <LowerThird s={s} name="WATCHING THE LINE" sub="QC MARKER" nameS={45.348} subS={45.348} outS={47.7} /> },
  // item 6 · BigStat · center · 59.8-64.0
  { from: 59.8, to: 64.0, node: (s) => <BigStat s={s} label="RAW POTATOES PROCESSED" value={750} valueS={60.163} decimals={0} suffix=" TONS" sub="every single day · single European facility" outS={64.0} /> },
  // item 7 · BigStat · center · 69.5-73.7
  { from: 69.5, to: 73.7, node: (s) => <BigStat s={s} label="PEPSICO POTATO SOURCING" value={240000} valueS={69.857} decimals={0} suffix=" TONS / YEAR" sub="one of the largest potato buyers on Earth" outS={73.7} /> },
  // item 8 · EquationStack · upper-center · 74.9-81.3
  { from: 74.9, to: 81.3, node: (s) => <EquationStack s={s} l1="$20 BILLION · GLOBAL CHIP MARKET" op="→" l2="$4 BILLION · LAY'S ANNUAL SALES" l1S={75.279} opS={77.0} l2S={78.251} outS={81.3} /> },
  // item 9 · LowerThird · lower-center · 80.6-84.1
  { from: 80.6, to: 84.1, node: (s) => <LowerThird s={s} name="WALKERS · LEICESTER, ENGLAND" sub="FACILITY" nameS={80.945} subS={80.945} outS={84.1} /> },
  // item 10 · Stamp · center · 96.6-99.5
  { from: 96.6, to: 99.5, node: (s) => <Stamp s={s} title="WEAPONS-GRADE" sub="MANUFACTURING PLANT" atS={96.932} outS={99.5} color={PRIMARY} rotate={-7} /> },
  // item 11 · LowerThird · lower-center · 104.4-108.5
  { from: 104.4, to: 108.5, node: (s) => <LowerThird s={s} name="MOON'S LAKE HOUSE · SARATOGA SPRINGS, NY" sub="1853 · ORIGIN" nameS={104.734} subS={104.734} outS={108.5} /> },
  // item 12 · LowerThird · lower-center · 114.2-117.3
  { from: 114.2, to: 117.3, node: (s) => <LowerThird s={s} name="GEORGE CRUM" sub="CHEF" nameS={114.579} subS={114.579} outS={117.3} /> },
  // item 13 · LowerThird · lower-center · 124.3-127.6
  { from: 124.3, to: 127.6, node: (s) => <LowerThird s={s} name="SARATOGA CHIPS" sub="1853 · BIRTH" nameS={124.68} subS={124.68} outS={127.6} /> },
  // item 14 · LowerThird · lower-center · 142.1-145.5
  { from: 142.1, to: 145.5, node: (s) => <LowerThird s={s} name="HERMAN LAY · AMERICAN SOUTH" sub="1930s · DSD ROUTE" nameS={142.501} subS={142.501} outS={145.5} /> },
  // item 15 · EquationStack · upper-center · 156.2-164.9
  { from: 156.2, to: 164.9, node: (s) => <EquationStack s={s} l1="FRITO + LAY · MERGED 1961" op="→" l2="PEPSICO ACQUIRED · 1965" l1S={156.595} opS={159.59} l2S={161.669} outS={164.9} /> },
  // item 16 · BigStat · upper-center · 169.9-172.2
  { from: 169.9, to: 172.2, node: (s) => <BigStat s={s} label="FLAVOR ARMS RACE · 1958 → TODAY" value={200} valueS={170.202} decimals={0} suffix=" VARIETIES" sub="worldwide · started with BBQ in 1958" outS={172.2} /> },
  // item 17 · Chapter · center · 172.6-174.4
  { from: 172.6, to: 174.4, node: (s) => <Chapter s={s} num="STEP 01" title="RECEIVING & INTAKE" numS={172.977} titleS={173.615} outS={174.4} /> },
  // item 18 · BigStat · center · 176.5-180.3
  { from: 176.5, to: 180.3, node: (s) => <BigStat s={s} label="SINGLE TRUCK · TRAILER" value={25} valueS={176.82} decimals={0} suffix=" TONS" sub="raw potatoes cascaded onto receiving conveyor" outS={180.3} /> },
  // item 19 · LowerThird · lower-center · 183.1-186.0
  { from: 183.1, to: 186.0, node: (s) => <LowerThird s={s} name="TILTED · 30°" sub="HYDRAULIC PLATFORM" nameS={183.472} subS={183.472} outS={186.0} /> },
  // item 20 · Stamp · lower-right → center · 193.4-196.3
  { from: 193.4, to: 196.3, node: (s) => <Stamp s={s} title="HYDROMETER" sub="STARCH : WATER RATIO" atS={193.794} outS={196.3} color={PRIMARY} rotate={-6} /> },
  // item 21 · LowerThird · lower-center · 221.9-230.8
  { from: 221.9, to: 230.8, node: (s) => <LowerThird s={s} name="CLASS ONE ALLERGEN RECALL · WRONG FILM ROLL" sub="2020 · VANCOUVER, WA" nameS={222.284} subS={222.284} outS={230.8} /> },
  // item 22 · Chapter · center · 235.7-237.2
  { from: 235.7, to: 237.2, node: (s) => <Chapter s={s} num="STEP 02" title="WASHING & PEELING" numS={236.099} titleS={236.738} outS={237.2} /> },
  // item 23 · BigStat · center · 252.5-256.2
  { from: 252.5, to: 256.2, node: (s) => <BigStat s={s} label="SIX INDUSTRIAL PEELERS" value={4} valueS={252.923} decimals={0} suffix=" MILLION / DAY" sub="potatoes processed at a major facility" outS={256.2} /> },
  // item 24 · EquationStack · upper-center · 261.1-266.3
  { from: 261.1, to: 266.3, node: (s) => <EquationStack s={s} l1="$300,000 · WEIGH SYSTEM" op="→" l2="MACHINE-LEARNING VISION" l1S={261.433} opS={263.0} l2S={263.638} outS={266.3} /> },
  // item 25 · HookText · lower-center → center · 308.1-311.5
  { from: 308.1, to: 311.5, node: (s) => <HookText s={s} text="HIT SUBSCRIBE" atS={308.464} outS={311.5} /> },
  // item 26 · Chapter · center · 311.8-313.4
  { from: 311.8, to: 313.4, node: (s) => <Chapter s={s} num="STEP 03" title="SLICING & STARCH REMOVAL" numS={312.202} titleS={312.887} outS={313.4} /> },
  // item 27 · LowerThird · lower-center · 316.6-320.0
  { from: 316.6, to: 320.0, node: (s) => <LowerThird s={s} name="CENTRIFUGAL DRUM SLICER" sub="MECHANISM" nameS={316.997} subS={316.997} outS={320.0} /> },
  // item 28 · EquationStack · upper-center · 326.6-333.0
  { from: 326.6, to: 333.0, node: (s) => <EquationStack s={s} l1="1 POTATO" op="→" l2="35 SLICES · 0.06 IN THICK" l1S={326.97} opS={328.0} l2S={329.292} outS={333.0} /> },
  // item 29 · Stamp · lower-right → center · 341.2-344.3
  { from: 341.2, to: 344.3, node: (s) => <Stamp s={s} title="SADDLE CURVE" sub="GEOMETRIC SWEET SPOT" atS={341.518} outS={344.3} color={ACCENT_GOLD} rotate={-5} /> },
  // item 30 · TextHighlight · upper-center · 378.0-383.5
  { from: 378.0, to: 383.5, node: (s) => <TextHighlight s={s} pre="directly linked to" hi="MAILLARD → ACRYLAMIDE" post="" hiS={380.052} hiColor={ACCENT_GOLD} outS={383.5} /> },
  // item 31 · BigStat · center · 387.6-391.7
  { from: 387.6, to: 391.7, node: (s) => <BigStat s={s} label="ACRYLAMIDE FORMS ABOVE" value={120} valueS={388.028} decimals={0} suffix="°C" sub="starchy foods heated above this point" outS={391.7} /> },
  // item 32 · LowerThird · lower-center · 389.6-393.7
  { from: 389.6, to: 393.7, node: (s) => <LowerThird s={s} name="EU BINDING REGULATION · ACRYLAMIDE" sub="2017" nameS={390.025} subS={390.025} outS={393.7} /> },
  // item 33 · Chapter · center · 408.4-410.0
  { from: 408.4, to: 410.0, node: (s) => <Chapter s={s} num="STEP 04" title="CONTINUOUS FRYING" numS={408.787} titleS={409.565} outS={410.0} /> },
  // item 34 · EquationStack · upper-center · 414.7-424.7
  { from: 414.7, to: 424.7, node: (s) => <EquationStack s={s} l1="FRYER · 40 FT LONG" op="·" l2="175–190°C · 3 MIN" l1S={415.01} opS={417.0} l2S={417.901} outS={424.7} /> },
  // item 35 · Stamp · center · 432.5-435.8
  { from: 432.5, to: 435.8, node: (s) => <Stamp s={s} title="50 / 50 OIL BLEND" sub="TRADE SECRET" atS={432.89} outS={435.8} color={PRIMARY} rotate={-8} /> },
  // item 36 · Chapter · center · 473.6-475.3
  { from: 473.6, to: 475.3, node: (s) => <Chapter s={s} num="STEP 05" title="DE-OIL · SEASON · SORT" numS={474.035} titleS={474.883} outS={475.3} /> },
  // item 37 · BigStat · center · 499.7-504.0
  { from: 499.7, to: 504.0, node: (s) => <BigStat s={s} label="OPTICAL SORTER · SCAN RATE" value={400} valueS={500.1} decimals={0} suffix=" CHIPS / SEC" sub="compressed-air ejection · mm accuracy" outS={504.0} /> },
  // item 38 · Stamp · center · 521.5-526.7
  { from: 521.5, to: 526.7, node: (s) => <Stamp s={s} title="GREEN MARKER CHIP" sub="MEASURES FRYER TRANSIT TIME" atS={521.857} outS={526.7} color={ACCENT_GOLD} rotate={-6} /> },
  // item 39 · Chapter · center · 552.8-554.5
  { from: 552.8, to: 554.5, node: (s) => <Chapter s={s} num="STEP 06" title="WEIGH · BAG · NITROGEN FLUSH" numS={553.216} titleS={554.086} outS={554.5} /> },
  // item 40 · Stamp · lower-right → center · 555.2-558.5
  { from: 555.2, to: 558.5, node: (s) => <Stamp s={s} title="N₂ FLUSH" sub="OXYGEN → NEAR ZERO" atS={555.596} outS={558.5} color={PRIMARY} rotate={-7} /> },
  // item 41 · EquationStack · upper-center · 567.6-573.0
  { from: 567.6, to: 573.0, node: (s) => <EquationStack s={s} l1="1 OZ · SNACK SIZE" op="↔" l2="13 OZ · PARTY PACK" l1S={567.972} opS={569.0} l2S={569.876} outS={573.0} /> },
  // item 42 · TextHighlight · center · 595.5-600.5
  { from: 595.5, to: 600.5, node: (s) => <TextHighlight s={s} pre="oxygen causes the oil to oxidize and turn" hi="RANCID" post="" hiS={597.926} hiColor={PRIMARY} outS={600.5} /> },
  // item 43 · LowerThird · lower-center · 620.3-623.5
  { from: 620.3, to: 623.5, node: (s) => <LowerThird s={s} name="SCHUBERT TLM · CARBON FIBER ARMS" sub="WALKERS · LEICESTER" nameS={620.646} subS={620.646} outS={623.5} /> },
  // item 44 · EquationStack · center · 643.9-650.0
  { from: 643.9, to: 650.0, node: (s) => <EquationStack s={s} l1="COST TO PRODUCE · <50¢" op="→" l2="RETAIL · $4 – $7" l1S={644.249} opS={645.5} l2S={646.78} outS={650.0} /> },
  // item 45 · Stamp · upper-center → center · 674.2-691.0
  { from: 674.2, to: 691.0, node: (s) => <Stamp s={s} title="NOT POTATO CHIPS" sub="FDA: PRINGLES = POTATO CRISPS" atS={674.609} outS={691.0} color={PRIMARY} rotate={-6} /> },
  // item 46 · LowerThird · lower-center · 698.2-702.1
  { from: 698.2, to: 702.1, node: (s) => <LowerThird s={s} name="HYPERBOLIC PARABOLOID" sub="PRINGLES SHAPE" nameS={698.561} subS={698.561} outS={702.1} /> },
  // item 47 · BigStat · center · 715.8-720.5
  { from: 715.8, to: 720.5, node: (s) => <BigStat s={s} label="COLD STORAGE · 45°F" value={40} valueS={716.231} decimals={0} suffix=" MILLION POTATOES" sub="single cellar holding" outS={720.5} /> },
  // item 48 · EndCard · center · 743.5-759.3
  { from: 743.5, to: 759.3, node: (s) => <EndCard s={s} atS={743.851} outS={759.3} title="50¢ → $5" sub="THAT IS HOW LAY'S ARE REALLY MADE" /> },
];

export const LaysFullPreview: React.FC = () => {
  return (
    <AbsoluteFill>
      {ITEMS.map((it, i) => (
        <Sequence key={i} from={sToF(it.from)} durationInFrames={Math.max(1, sToF(it.to) - sToF(it.from))} layout="none">
          {it.node(it.from)}
        </Sequence>
      ))}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
